import { useDispatch } from 'react-redux';
import {
  GIS_LAYER_SEARCH,
  GIS_SEARCH,
} from '@rsos/capstone/src/constants/capabilities';
import { PREMIUM_GIS_SEARCH } from '@rsos/constants/permissions';
import useHasCapability from '@rsos/hooks/useHasCapability';
import useHasPermission from '@rsos/hooks/useHasPermission';
import { search } from '../UniversalSearch/searchSlice';
import styles from './UnplottableAddressNotice.module.css';
import { requestMapAddressSearch } from './requestMapAddressSearch';

type NoticeVariant = 'queue' | 'card';

interface UnplottableAddressNoticeProps {
  address: string;
  variant?: NoticeVariant;
}

/**
 * Tells the dispatcher the ALI address arrived and cannot be plotted,
 * and offers a GIS search of that address.
 */
const UnplottableAddressNotice = ({
  address,
  variant = 'card',
}: UnplottableAddressNoticeProps) => {
  const dispatch = useDispatch();
  const { hasCapability: hasLayerSearchCapability } =
    useHasCapability(GIS_LAYER_SEARCH);
  const { hasPermission: hasLayerSearchPermission } =
    useHasPermission('GIS_LAYER_SEARCH');
  const { hasCapability: hasGisSearchCapability } =
    useHasCapability(GIS_SEARCH);
  const { hasPermission: hasGisSearchPermission } =
    useHasPermission(PREMIUM_GIS_SEARCH);
  const hasLayerSearch = hasLayerSearchCapability && hasLayerSearchPermission;
  const hasGisSearch = hasGisSearchCapability && hasGisSearchPermission;

  return (
    <div
      className={`${styles.notice} ${variant === 'queue' ? styles.queue : ''}`}
      data-name="unplottable-address"
    >
      {variant === 'queue' ? (
        <p className={styles.label}>Not on map</p>
      ) : (
        <>
          <p className={styles.note}>
            Address received. It could not be placed on the map.
          </p>
          {(hasLayerSearch || hasGisSearch) && (
            <button
              type="button"
              className={styles.search}
              data-name="search-address-on-map"
              onClick={() => {
                if (hasLayerSearch) {
                  requestMapAddressSearch(address);
                  return;
                }
                dispatch(search({ searchTerm: address }));
              }}
            >
              Search on map
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default UnplottableAddressNotice;
